import { useCallback, useEffect, useRef } from "react";

import { trackEvent, type EventType, type TrackingProperties } from "@/shared/utils/core";
import { secondsSince } from "@/shared/utils/funnelAnalytics";

type EventTypeValue = (typeof EventType)[keyof typeof EventType];

interface StepAnalyticsEvents {
  viewed: EventTypeValue;
  completed: EventTypeValue;
  back: EventTypeValue;
}

interface UseStepAnalyticsOptions {
  stepId: string | null;
  stepNumber?: number;
  events: StepAnalyticsEvents;
  properties: TrackingProperties;
  isResume?: boolean;
}

interface ActiveStep {
  id: string;
  number?: number;
  enteredAt: number;
}

export function useStepAnalytics({
  stepId,
  stepNumber,
  events,
  properties,
  isResume = false,
}: UseStepAnalyticsOptions) {
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;
  const stepNumberRef = useRef(stepNumber);
  stepNumberRef.current = stepNumber;
  const isResumeRef = useRef(isResume);
  isResumeRef.current = isResume;
  const hasViewedRef = useRef(false);
  const activeStepRef = useRef<ActiveStep | null>(null);
  const skipNextCompletionRef = useRef(false);

  useEffect(() => {
    const previous = activeStepRef.current;
    if (previous?.id !== stepId) {
      if (previous && !skipNextCompletionRef.current) {
        trackEvent(eventsRef.current.completed, {
          ...propertiesRef.current,
          step_id: previous.id,
          step_number: previous.number,
          time_on_step_seconds: secondsSince(previous.enteredAt),
          next_step_id: stepId ?? undefined,
        });
      }
      skipNextCompletionRef.current = false;

      if (stepId) {
        trackEvent(eventsRef.current.viewed, {
          ...propertiesRef.current,
          step_id: stepId,
          step_number: stepNumberRef.current,
          is_resume: !hasViewedRef.current && isResumeRef.current,
        });
        hasViewedRef.current = true;
        activeStepRef.current = {
          id: stepId,
          number: stepNumberRef.current,
          enteredAt: Date.now(),
        };
      } else {
        activeStepRef.current = null;
      }
    }
  }, [stepId]);

  const trackStepBack = useCallback(() => {
    const active = activeStepRef.current;
    if (active) {
      trackEvent(eventsRef.current.back, {
        ...propertiesRef.current,
        step_id: active.id,
        time_on_step_seconds: secondsSince(active.enteredAt),
      });
      skipNextCompletionRef.current = true;
    }
  }, []);

  const trackFinalStepCompleted = useCallback(() => {
    const active = activeStepRef.current;
    if (active) {
      trackEvent(eventsRef.current.completed, {
        ...propertiesRef.current,
        step_id: active.id,
        step_number: active.number,
        time_on_step_seconds: secondsSince(active.enteredAt),
      });
      activeStepRef.current = null;
    }
  }, []);

  const skipNextCompletion = useCallback(() => {
    skipNextCompletionRef.current = true;
  }, []);

  const getActiveStepId = useCallback(() => activeStepRef.current?.id, []);

  return { trackStepBack, trackFinalStepCompleted, skipNextCompletion, getActiveStepId };
}
